import { useCallback } from 'react'
// Raw router navigation is intentionally isolated here so every imperative route change
// observes the same modified-click behavior.
// eslint-disable-next-line no-restricted-imports
import { createPath, resolvePath, useLocation, useNavigate } from 'react-router-dom'
import type { NavigateFunction, NavigateOptions, To } from 'react-router-dom'
import { openRouteInNewTab, shouldOpenRouteInNewTab } from '../logic/routeNavigationIntent'

/**
 * The only supported imperative route-navigation hook in the app. Prefer Link/NavLink for
 * destinations that are known while rendering; use this for workflow-driven navigation.
 */
export function useRouteNavigate(): NavigateFunction {
  const navigate = useNavigate()
  const location = useLocation()

  const routeNavigate = useCallback(
    (to: To | number, options?: NavigateOptions) => {
      if (typeof to === 'number') {
        navigate(to)
        return
      }

      const destination = resolvePath(to, location.pathname)
      const destinationPath = createPath(destination)
      const currentPath = `${location.pathname}${location.search}${location.hash}`

      if (shouldOpenRouteInNewTab() && destinationPath !== currentPath) {
        openRouteInNewTab(
          new URL(destinationPath, window.location.origin).toString(),
          options?.state
        )
        return
      }

      navigate(to, options)
    },
    [location.hash, location.pathname, location.search, navigate]
  )

  return routeNavigate as NavigateFunction
}
